import {
    ForSaleLevel,
    PurchaseProductRequestDTO,
    ProductBillingFrequency,
    ReturnMessage,
    CBPortalSessionDTO,
    BillingAccountDTO,
    ErrorMessage,
    CBCouponDTO,
    CouponItemType
} from '../types';
import BaseAPI from "./BaseAPI";
import { APIError, isErrorMessage } from "../util/Exceptions";

export default class BillingAPI extends BaseAPI {

    static async createBillingAccount(account_guid: string, user_guid: string): Promise<BillingAccountDTO> {
        const path = '/billing/create-cb-account';
        const request_body = {
            account_guid,
            user_guid
        }

        try {
            const data = await BaseAPI.callPost(path, request_body);
            return data.payload as BillingAccountDTO;
        } catch (error) {
            console.error(error);
            if (isErrorMessage(error)) {
                throw new APIError(error); // Propagate the error so the caller can handle it
            } else {
                throw error;
            }
        }
    }

    static async getCurrentForSaleProducts(): Promise<ForSaleLevel[]> {
        const path = '/billing/cb-current-products';
        try {
            const data = await BaseAPI.callGET(path);
            return data.payload as ForSaleLevel[];
        } catch (error) {
            console.error(error);
            if (isErrorMessage(error)) {
                throw new APIError(error); // Propagate the error so the caller can handle it
            } else {
                throw error;
            }
        }
    }

    static async purchaseProductRequest(request: PurchaseProductRequestDTO, subscription_billing_frequency: ProductBillingFrequency | null): Promise<ReturnMessage> {
        const path = '/billing/purchase-products';

        // Check if more than one subscription is present
        if (request.purchased_products.subscriptions.length > 1) {
            throw new Error("Only one subscription can be processed at a time.");
        }

        // Validate and apply filtering if subscription_billing_frequency is not null
        if (subscription_billing_frequency) {
            request.purchased_products.subscriptions.forEach(subscription => {
                const hasValidFrequencyInPricing = subscription.pricing.some(price => price.billing_frequency === subscription_billing_frequency);
                const hasValidFrequencyInAddons = subscription.allowed_addons.every(addon =>
                    addon.pricing.some(price => price.billing_frequency === subscription_billing_frequency)
                );

                if (!hasValidFrequencyInPricing || !hasValidFrequencyInAddons) {
                    throw new Error("The subscription_billing_frequency must be available in both the subscription and its addons.");
                }

                // Filter pricing to match the billing frequency
                subscription.pricing = subscription.pricing.filter(price => price.billing_frequency === subscription_billing_frequency);

                // Directly filter the pricing of each allowed addon
                subscription.allowed_addons.forEach(addon => {
                    addon.pricing = addon.pricing.filter(price => price.billing_frequency === subscription_billing_frequency);
                });
            });
        }

        try {
            return await BaseAPI.callPost(path, request);
        } catch (error) {
            console.error(error);
            if (isErrorMessage(error)) {
                throw new APIError(error); // Propagate the error so the caller can handle it
            } else {
                throw error;
            }
        }
    }

    static async getPortalSession(account_guid: string): Promise<CBPortalSessionDTO> {
        const path = `/billing/cb-get-portal-session/${account_guid}`;
        try {
            return await BaseAPI.callGET(path) as CBPortalSessionDTO;
        } catch (error) {
            console.error(error);
            if (isErrorMessage(error)) {
                throw new APIError(error); // Propagate the error so the caller can handle it
            } else {
                throw error;
            }
        }
    }

    static async getBillingAccount(account_guid: string): Promise<BillingAccountDTO> {
        const path = `/billing/cb-get-billing-account/${account_guid}`;
        try {
            return await BaseAPI.callGET(path) as BillingAccountDTO;
        } catch (error) {
            console.error(error);
            if (isErrorMessage(error)) {
                throw new APIError(error); // Propagate the error so the caller can handle it
            } else {
                throw error;
            }
        }
    }

    static async getCoupon(coupon_code: string, item_type?: CouponItemType): Promise<CBCouponDTO> {
        const path = `/billing/cb-get-coupon`;
        let queryParams: any = {};

        queryParams.coupon_code = coupon_code;
        if (item_type) {
            queryParams.item_type = item_type;
        }
  
        try {
            return await BaseAPI.callGET(path, queryParams) as CBCouponDTO;
        } catch (error) {
            console.error(error);
            if (isErrorMessage(error)) {
                throw new APIError(error); // Propagate the error so the caller can handle it
            } else {
                throw error;
            }
        }
    }

}
